/**
 * RawHtmlViewer - fetch raw HTML tu R2 stream va render trong iframe srcdoc.
 *
 * Spec: specs/M2-EXECUTION-PLAN-v2.md §4 P-A5 AC-7 + AC-8 + AC-9.
 *
 * Pipeline:
 *   1. fetchRawHtml(docId) -> html text tu /api/v1/doc/:id/raw-html (R2 stream)
 *   2. sanitizeRawHtml -> DOMPurify allowlist (cf dompurify-config.ts)
 *   3. injectAnchorIds -> them id="dieu-N" anchor cho TOC scroll
 *   4. buildIframeSrcDoc -> wrap voi CSS normalize Times New Roman -> Inter
 *   5. iframe sandbox="allow-same-origin" (KHONG allow-scripts)
 *   6. Parse TOC entry, callback onTocReady cho parent
 *   7. Imperative jump-to-anchor via iframe.contentDocument
 *
 * Lifecycle:
 *   - Mount: setState loading, fetch HTML
 *   - On HTML ready: sanitize + inject + parse TOC + setState ready
 *   - On unmount: cancel fetch
 *
 * Fallback: neu TOC parse 0 entry -> notify parent (FallbackBanner se render).
 */

import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { fetchRawHtml } from '../../lib/corpus-api';
import { sanitizeRawHtml, buildIframeSrcDoc } from '../../lib/dompurify-config';
import { parseTocFromHtml, injectAnchorIds, type TocEntry } from './TocSidebar';

interface Props {
  docId: string;
  onTocReady?: (entries: TocEntry[]) => void;
  onError?: (err: Error) => void;
}

export interface RawHtmlViewerHandle {
  jumpToAnchor: (anchorId: string) => void;
}

export const RawHtmlViewer = forwardRef<RawHtmlViewerHandle, Props>(function RawHtmlViewer(
  { docId, onTocReady, onError },
  ref,
) {
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'empty'>('loading');
  const [srcDoc, setSrcDoc] = useState<string>('');
  // Iframe height auto-grows to content so the page has a single scroll
  // bar instead of nested iframe+page scrolling (founder feedback
  // 2026-05-27). Min keeps loading-state from collapsing; max guards
  // against pathological docs (very rare > 20000px) and signals "scroll
  // inside iframe" instead of taking over the whole viewport.
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const tocReadyRef = useRef(onTocReady);
  const errorRef = useRef(onError);

  // Keep ref updated without re-firing fetch effect.
  useEffect(() => {
    tocReadyRef.current = onTocReady;
    errorRef.current = onError;
  }, [onTocReady, onError]);

  useEffect(() => {
    if (!docId) {
      setState('error');
      return;
    }
    let cancelled = false;
    setState('loading');
    setSrcDoc('');

    fetchRawHtml(docId)
      .then((html) => {
        if (cancelled) return;
        const trimmed = (html || '').trim();
        if (!trimmed) {
          setState('empty');
          tocReadyRef.current?.([]);
          return;
        }
        // Sanitize first to remove unsafe tag/attr.
        const sanitized = sanitizeRawHtml(trimmed);
        // Inject anchor id for Dieu/Chuong.
        const withAnchors = injectAnchorIds(sanitized);
        // Parse TOC AFTER anchor injection (use original sanitized HTML for
        // parsing because anchors are already added by injectAnchorIds).
        const entries = parseTocFromHtml(withAnchors);
        // Build srcDoc payload.
        const doc = buildIframeSrcDoc(withAnchors);
        setSrcDoc(doc);
        setState('ready');
        tocReadyRef.current?.(entries);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState('error');
        errorRef.current?.(err);
      });

    return () => {
      cancelled = true;
    };
  }, [docId]);

  useImperativeHandle(
    ref,
    () => ({
      jumpToAnchor(anchorId: string) {
        const iframe = iframeRef.current;
        if (!iframe || !anchorId) return;
        try {
          const doc = iframe.contentDocument;
          if (!doc) return;
          const target = doc.getElementById(anchorId);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } catch {
          // Same-origin iframe srcdoc nen no error; fallback silent.
        }
      },
    }),
    [],
  );

  const jumpFromHash = useCallback((anchorId: string) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const target = doc.getElementById(anchorId);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {
      // ignore
    }
  }, []);

  // On mount, if URL hash present (e.g. #dieu-5), jump after iframe load.
  useEffect(() => {
    if (state !== 'ready') return;
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (!hash) return;
    const anchorId = hash.replace(/^#/, '');
    if (!anchorId) return;
    // Wait a tick for iframe to mount srcdoc.
    const t = setTimeout(() => jumpFromHash(anchorId), 250);
    return () => clearTimeout(t);
  }, [state, jumpFromHash]);

  // Auto-size iframe to its content so the page has a single scroll bar.
  // srcdoc is same-origin so we can read contentDocument directly. Re-measure
  // on iframe load + ResizeObserver on its <body> for fonts/images that
  // settle after first paint.
  useEffect(() => {
    if (state !== 'ready') return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    let observer: ResizeObserver | null = null;
    let raf = 0;

    const measure = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.max(
          doc.documentElement?.scrollHeight ?? 0,
          doc.body?.scrollHeight ?? 0
        );
        if (h > 0) setContentHeight(h);
      } catch {
        // Same-origin srcdoc shouldn't throw; ignore.
      }
    };

    const onLoad = () => {
      measure();
      try {
        const body = iframe.contentDocument?.body;
        if (body && typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(() => {
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(measure);
          });
          observer.observe(body);
        }
      } catch {
        // ignore
      }
    };

    iframe.addEventListener('load', onLoad);
    // srcdoc may already be loaded by the time effect runs.
    if (iframe.contentDocument?.readyState === 'complete') onLoad();

    return () => {
      iframe.removeEventListener('load', onLoad);
      if (observer) observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [state, srcDoc]);

  if (state === 'loading') {
    return (
      <div
        aria-busy
        aria-label="Đang tải nội dung văn bản"
        style={{
          padding: 32,
          background: 'var(--asklaw-paper)',
          border: '1px solid var(--asklaw-border)',
          borderRadius: 12,
          minHeight: 200,
          color: 'var(--asklaw-muted)',
          fontSize: 14,
        }}
      >
        Đang tải nội dung văn bản từ vbpl.vn...
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div
        role="alert"
        style={{
          padding: 20,
          border: '1px solid #fecaca',
          background: '#fef2f2',
          borderRadius: 12,
          color: '#991b1b',
          fontSize: 14,
        }}
      >
        Không tải được nội dung văn bản. Vui lòng thử lại sau hoặc xem bản gốc tại tab "Văn bản gốc".
      </div>
    );
  }

  if (state === 'empty') {
    return (
      <div
        role="status"
        style={{
          padding: 24,
          background: 'var(--asklaw-cream-deep)',
          border: '1px dashed var(--asklaw-border)',
          borderRadius: 12,
          color: 'var(--asklaw-muted)',
          fontSize: 14,
          textAlign: 'center',
        }}
      >
        Văn bản này chưa có nội dung số hoá. Vui lòng xem bản gốc tại tab "Văn bản gốc".
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      title="Nội dung văn bản pháp luật"
      srcDoc={srcDoc}
      sandbox="allow-same-origin"
      scrolling="no"
      style={{
        width: '100%',
        // Grow to content height after first measure so the page scrolls
        // as one unit. Fall back to 70vh while measuring (first paint).
        height: contentHeight ? `${contentHeight}px` : '70vh',
        minHeight: '70vh',
        border: '1px solid var(--asklaw-border)',
        borderRadius: 12,
        background: '#fff',
        display: 'block',
        overflow: 'hidden',
      }}
    />
  );
});
